<template>
  <dp-container v-if="windowWidth > 991">
    <!-- Desktop Screen -->
    <ValidationObserver ref="essential_style_form" slim>
      <form class="mx-4 mt-4">
        <section class="mb-3">
          <ProductConfigBackLink product-type="Essentials" />
        </section>
        <h1 v-if="productStyle" class="text-center">
          {{ productStyle.name }}
        </h1>

        <div v-if="selectedProduct && selectedProduct.price">
          <h2 v-if="selectedProduct.discountedPrice" class="text-center">
            <del>${{ selectedProduct.price.toFixed(2) }}</del>
            <span class="ml-2 discounted-price"> ${{ selectedProduct.discountedPrice.toFixed(2) }}</span>
          </h2>
          <h2 v-else-if="selectedProduct.price" class="text-center">${{ selectedProduct.price.toFixed(2) }}</h2>
        </div>
        <div v-else>
          <h2 v-if="productStyle && productStyle.discountedPrice" class="text-center">
            <del>${{ productStyle.price.toFixed(2) }}</del>
            <span class="ml-2 discounted-price"> ${{ productStyle.discountedPrice.toFixed(2) }}</span>
          </h2>
          <h2 v-else-if="productStyle && productStyle.price" class="text-center">${{ productStyle.price.toFixed(2) }}</h2>
        </div>

        <b-row class="mt-lg-5">
          <!-- Desktop Preview Column -->
          <b-col :sm="12" :md="12" :lg="5" :xl="5">
            <carousel
              :images="images"
              :auto-slide="!color"
              :curr-image-idx="curImageIdx"
              class="mt-sm-4 mt-md-4 mt-lg-0 mt-xl-0 mb-sm-4 mb-md-4 mb-lg-0 mb-xl-0 sticky"
            />
          </b-col>
          <b-col :sm="12" :md="12" :lg="6" :xl="6" :offset-lg="1" :offset-xl="1">
            <!-- (Description and Select Colour, Size, etc...) -->
            <section class="mb-5">
              <hr />

              <!-- Description -->
              <h2 v-b-toggle.description class="align-items-center">
                {{ $t('dp.generic.description') }}
                <fa-icon :icon="['fa', 'chevron-down']" size="xs" class="float-right when-closed mt-1" />
                <fa-icon :icon="['fa', 'chevron-up']" size="xs" class="float-right when-open mt-1" />
              </h2>

              <b-collapse id="description" visible>
                <p v-html="productStyle && productStyle.description" />
              </b-collapse>

              <hr />

              <essentials-color-select
                v-if="colorOptions.length > 1"
                ref="essential_style_color_select"
                v-model="color"
                class="mb-4"
                :options="colorOptions"
                auto-select-on-create
              />

              <essentials-size-select
                v-if="hasSizeOptions"
                ref="essential_style_size_select"
                v-model="size"
                class="mb-4"
                :sizes="sizeOptions"
              />
            </section>

            <!-- Quantity -->
            <ValidationProvider
              v-if="selectedProduct && selectedProduct.status === 'I'"
              v-slot="{ errors, valid, validated, dirty }"
              ref="validationProvider"
              name="quantity"
              :rules="{ required: true }"
            >
              <h3
                v-if="selectedProduct.maxQuantity <= 10"
                class="mb-2"
                style="color: darkgreen"
                v-text="$t('essentials.limitedStock', [selectedProduct.maxQuantity])"
              />
              <b-form-group label="Quantity:" label-for="quantity-input" label-cols="4" label-cols-lg="2" label-size="lg" class="text-dark">
                <b-form-select
                  id="quantity-input"
                  v-model="quantity"
                  class="quantity-select ml-3"
                  name="quantity"
                  :options="[
                    ...Array.from(
                      {
                        length: selectedProduct && selectedProduct.hasOwnProperty('maxQuantity') ? selectedProduct.maxQuantity || 1 : 20,
                      },
                      (_, i) => {
                        return { value: i + 1, text: i + 1 };
                      }
                    ),
                  ]"
                  v-bind="$attrs"
                  :class="errors[0] ? 'is-invalid' : ''"
                  :state="dirty || validated ? valid : null"
                  v-on="$listeners"
                />
                <b-form-invalid-feedback v-html="errors[0]" />
              </b-form-group>
            </ValidationProvider>

            <div v-if="(selectedProduct && selectedProduct.status !== 'I') || allProductsOutOfStock">
              <span v-text="$t('essentials.outOfStock.text')" />&nbsp;
              <b-link
                v-if="allProductsOutOfStock"
                :to="{ name: 'EssentialsStore' }"
                class="text-decoration-none"
                v-text="$t('essentials.outOfStock.linkText')"
              />
            </div>
            <b-button
              id="product-style-add-to-basket"
              variant="primary"
              :disabled="allProductsOutOfStock || (selectedProduct && selectedProduct.status !== 'I') || itemLoading"
              block
              @click="validateBeforeAddItem"
            >
              {{ $t('product.addToBasket') }}
              <div v-if="itemLoading" role="status" class="fa-pulse float-right mt-1">
                <fa-icon :icon="['fas', 'spinner']" class="d-flex" />
                <span class="sr-only">{{ $t('dp.buttons.loading') }}</span>
              </div>
            </b-button>

            <!-- DISCLAIMER -->
            <small v-if="productStyle && productStyle.disclaimer" class="d-block mt-3" v-html="productStyle.disclaimer" />
          </b-col>
        </b-row>
      </form>
    </ValidationObserver>
  </dp-container>
  <dp-container
    v-else-if="windowWidth <= 991 && productStyle && productStyle.id"
    class="dp-container-wrapper"
    :class="{ disabled: itemLoading }"
  >
    <!--      Mobile Screen -->
    <ValidationObserver ref="essential_style_form" slim>
      <form class="mx-4">
        <section class="mt-4">
          <section class="mb-3">
            <ProductConfigBackLink product-type="Essentials" />
          </section>
          <h1 class="text-center">
            {{ productStyle.name }}
          </h1>
          <h2 v-if="productStyle && productStyle.discountedPrice" class="text-center">
            <del>${{ productStyle.price.toFixed(2) }}</del>
            <span class="ml-2 discounted-price"> ${{ productStyle.discountedPrice.toFixed(2) }}</span>
          </h2>
          <h2 v-else-if="productStyle && productStyle.price" class="text-center">${{ productStyle.price.toFixed(2) }}</h2>
          <b-row class="mt-lg-5">
            <b-col :sm="12" :md="12" :lg="5" :xl="5">
              <carousel
                :images="images"
                :auto-slide="!color"
                :curr-image-idx="curImageIdx"
                class="mt-sm-4 mt-md-4 mt-lg-0 mt-xl-0 mb-sm-4 mb-md-4 mb-lg-0 mb-xl-0"
              />
            </b-col>
            <b-col :sm="12" :md="12" :lg="6" :xl="6" :offset-lg="1" :offset-xl="1">
              <!-- Step 1 (Select Collar Colour, Hardware Colour and Size) -->
              <section class="my-4">
                <ValidationObserver ref="essential_style_form_step_1" slim>
                  <hr />

                  <!-- Description -->
                  <h2 v-b-toggle.description class="align-items-center">
                    {{ $t('dp.generic.description') }}
                    <fa-icon :icon="['fa', 'chevron-down']" size="xs" class="float-right when-closed mt-1" />
                    <fa-icon :icon="['fa', 'chevron-up']" size="xs" class="float-right when-open mt-1" />
                  </h2>

                  <b-collapse id="description" visible>
                    <p v-html="productStyle && productStyle.description" />
                  </b-collapse>

                  <hr />

                  <essentials-color-select
                    v-if="colorOptions.length > 1"
                    ref="essential_style_color_select"
                    v-model="color"
                    class="mb-4"
                    :options="colorOptions"
                    auto-select-on-create
                  />

                  <essentials-size-select
                    v-if="hasSizeOptions"
                    ref="essential_style_color_select"
                    v-model="size"
                    class="mb-4"
                    :sizes="sizeOptions"
                  />
                </ValidationObserver>
              </section>

              <!-- Quantity -->
              <ValidationProvider
                v-if="selectedProduct && selectedProduct.status === 'I'"
                v-slot="{ errors, valid, validated, dirty }"
                ref="validationProvider"
                name="quantity"
                :rules="{ required: true }"
              >
                <h3
                  v-if="selectedProduct.maxQuantity <= 10"
                  class="mb-2"
                  style="color: darkgreen"
                  v-text="$t('essentials.limitedStock', [selectedProduct.maxQuantity])"
                />
                <b-form-group>
                  <h3 class="d-inline text-dark">{{ $t('dp.generic.quantity') }}:</h3>
                  <b-form-select
                    v-model="quantity"
                    class="quantity-select ml-3"
                    name="quantity"
                    :options="[
                      ...Array.from(
                        {
                          length: selectedProduct && selectedProduct.hasOwnProperty('maxQuantity') ? selectedProduct.maxQuantity || 1 : 20,
                        },
                        (_, i) => {
                          return { value: i + 1, text: i + 1 };
                        }
                      ),
                    ]"
                    v-bind="$attrs"
                    :class="errors[0] ? 'is-invalid' : ''"
                    :state="dirty || validated ? valid : null"
                    v-on="$listeners"
                  />
                  <b-form-invalid-feedback v-html="errors[0]" />
                </b-form-group>
              </ValidationProvider>

              <div v-if="(selectedProduct && selectedProduct.status !== 'I') || allProductsOutOfStock">
                <span v-text="$t('essentials.outOfStock.text')" />&nbsp;
                <b-link
                  v-if="allProductsOutOfStock"
                  :to="{ name: 'EssentialsStore' }"
                  class="text-decoration-none"
                  v-text="$t('essentials.outOfStock.linkText')"
                />
              </div>
              <b-button
                id="product-style-add-to-basket"
                variant="primary"
                block
                :disabled="allProductsOutOfStock || (selectedProduct && selectedProduct.status !== 'I') || itemLoading"
                @click="validateBeforeAddItem"
              >
                {{ $t('product.addToBasket') }}
                <div v-if="itemLoading" role="status" class="fa-pulse float-right mt-1">
                  <fa-icon :icon="['fas', 'spinner']" class="d-flex" />
                  <span class="sr-only">{{ $t('dp.buttons.loading') }}</span>
                </div>
              </b-button>

              <!-- DISCLAIMER -->
              <small v-if="productStyle && productStyle.disclaimer" class="d-block mt-3" v-html="productStyle.disclaimer" />
            </b-col>
          </b-row>
        </section>
      </form>
    </ValidationObserver>
  </dp-container>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import getImage from '@/mixins/getImage';
import { DpContainer, DpFocusableElementMixins } from '@docupet/component-library';
import Carousel from '@/components/Carousel';
import EssentialsSizeSelect from '@/components/essentials/EssentialsSizeSelect';
import EssentialsColorSelect from '@/components/essentials/EssentialsColorSelect';
import { NotificationMixins } from '@/mixins/notification';
import { ProductStyleAnalyticsMixins } from '@/mixins/analytics/productStyle';
import { ProductRedirectMixins } from '@/mixins/redirect';
import ProductConfigBackLink from '@/components/common/ProductConfigBackLink';

export default {
  name: 'EssentialStyle',
  components: {
    ProductConfigBackLink,
    EssentialsColorSelect,
    EssentialsSizeSelect,
    Carousel,
    DpContainer,
  },
  mixins: [getImage, NotificationMixins, ProductStyleAnalyticsMixins, ProductRedirectMixins, DpFocusableElementMixins],
  props: {
    styleId: {
      type: [String, Number],
      default: '',
      required: false,
    },
    groupId: {
      type: [String, Number],
      default: '',
      required: false,
    },
    petId: {
      type: [String, Number],
      default: '',
      required: false,
    },
  },
  data() {
    return {
      pet: '',
      size: '',
      style: this.productStyle?.name || '',
      color: '',
      windowWidth: window.innerWidth,
      quantity: 1,
    };
  },
  metaTag() {
    return {
      ...this.$router.currentRoute.meta,
      title: `${this.productStyle?.name || ''} -`,
      meta: [
        { charset: 'utf-8' },
        {
          vmid: 'description',
          name: 'description',
          content: this.productStyle?.description || '',
        },
        {
          vmid: 'og:description',
          property: 'og:description',
          content: this.productStyle?.description || '',
        },
      ],
    };
  },
  computed: {
    ...mapState({
      city: (state) => state.user.city,
      cityId: (state) => state.cityId,
      orderId: (state) => state.orderId,
      baseUrl: (state) => state.baseUrl,
      loading: (state) => state.essentialProduct?.loading,
      itemLoading: (state) => state.order.itemLoading,
      productGroups: (state) => state.essentialProduct?.groups || [],
      order: (state) => state.order.order,
      user: (state) => state.user.user,
      isAdmin: (state) => state.user.user.id && state.user.user.__typename == 'Administrator',
    }),
    ...mapGetters({
      generateFullUrl: 'generateFullUrl',
      localBasketCount: 'order/getLocalCount',
    }),
    productStyleCode() {
      return this.productStyle?.code;
    },
    productStyle() {
      const group = this.productGroups?.find?.((group) => group.id == this.groupId);

      if (!group) {
        return null;
      }

      return group?.productStyles?.find?.((style) => style.id == this.styleId);
    },
    images() {
      let images = [];
      this.productStyle?.products?.forEach((product) => {
        if (product?.color === this.color) images.push(product?.backImage || product?.image);
      });
      if (images.length <= 0) {
        images.push(this.productStyle?.products?.[0]?.backImage || this.productStyle?.products?.[0]?.image);
      }
      return images?.filter((image) => !!image && !!image?.original) || ['https://via.placeholder.com/300x300'];
    },
    curImageIdx() {
      return 0;
    },
    colorOptions() {
      let colors = [];
      this.productStyle?.products?.forEach((product) => {
        let index = colors?.findIndex((color) => color?.name === product?.color);
        if (index === -1) {
          index = colors?.length;
          colors?.push({ name: product.color });
        }

        if (!product.size) {
          colors[index].image = product?.image;
          colors[index].backImage = product?.backImage;
          colors[index].status = product?.status;
        } else {
          colors[index].image = product?.image;
          colors[index].backImage = product?.backImage;
          colors[index][`${product?.size?.toUpperCase()}Details`] = {
            description: product?.sizeDescription,
            image: product?.sizeImage?.original,
            length: product?.sizeLength,
            title: product?.sizeTitle,
            width: product?.sizeWidth,
          };
          colors[index][`${product?.size?.toUpperCase()}InStock`] = product?.status === 'I';
        }
      });
      colors.allOutOfStock = !colors.some((color) => color.SInStock || color.LInStock);
      return colors;
    },
    noColorsJustSizes() {
      return this.colorOptions.length === 1 && !this.colorOptions[0].name;
    },
    sizeOptions() {
      let sizes = {};
      let color = this.colorOptions?.find((color) => color?.name === this.color);
      if (color) {
        if (color.XSInStock !== undefined) sizes.XS = { inStock: !!color?.XSInStock, ...color?.XSDetails };
        if (color.SInStock !== undefined) sizes.S = { inStock: !!color?.SInStock, ...color?.SDetails };
        if (color.MInStock !== undefined) sizes.M = { inStock: !!color?.MInStock, ...color?.MDetails };
        if (color.LInStock !== undefined) sizes.L = { inStock: !!color?.LInStock, ...color?.LDetails };
        if (color.XLInStock !== undefined) sizes.XL = { inStock: !!color?.XLInStock, ...color?.XLDetails };
      } else if (this.hasSizeOptions) {
        return this.hasSizeOptions;
      }
      return sizes;
    },
    hasSizeOptions() {
      let hasSizes = false;
      let sizes = {};
      this.productStyle?.products?.forEach((product) => {
        if (product.size) {
          hasSizes = true;
          sizes[product.size.toUpperCase()] = { inStock: product.status === 'I' };
        }
      });
      return hasSizes ? sizes : hasSizes;
    },
    selectedProductId() {
      return this.selectedProduct?.id;
    },
    selectedProduct() {
      if (!this.hasSizeOptions && this.colorOptions.length <= 1) {
        return this.productStyle?.products[0];
      }
      if (!this.hasSizeOptions && this.colorOptions.length > 1) {
        return this.productStyle?.products?.find((product) => product?.color === this.color && product?.status === 'I');
      }
      const mapSize = { XS: 'xs', S: 's', M: 'm', L: 'l', XL: 'xl' };
      if (this.hasSizeOptions && this.colorOptions.length <= 1) {
        return this.productStyle?.products?.find((product) => product?.size === mapSize[this.size] && product?.status === 'I');
      }
      return this.productStyle?.products?.find(
        (product) => product?.color === this.color && product?.size === mapSize[this.size] && product?.status === 'I'
      );
    },
    defaultPhoneNumber() {
      return this.city?.customerService?.phoneNumber?.replace(/-/, '')?.substring(1) || '';
    },
    allProductsOutOfStock() {
      return this.productStyle?.products?.every((product) => product?.status === 'O');
    },
  },
  created() {
    window.addEventListener('resize', this.windowResize);
    (async () => {
      if (!this.productGroups || this.productGroups?.length === 0) {
        await this.$store.dispatch('essentialProduct/fetchGroups', { cityId: this.city?.id || this.cityId });
        while (this.loading) await new Promise((resolve) => setTimeout(resolve, 1000));
      }
    })().then(() => {
      if (!this.productStyle?.code && this.productGroups?.length !== 0) {
        this.$store
          .dispatch('essentialProduct/setStyle', this.productGroups?.find((group) => group.id == this.groupId)?.productStyles?.[0])
          .then(() => this.$refs?.['essential_style_color_select']?.setColor(this.productStyle?.code));
      } else {
        const findInStockColor = this.colorOptions?.find((color) => color.status === 'I')?.name;
        findInStockColor ? this.$refs?.['essential_style_color_select']?.setColor(findInStockColor) : null;
      }
    });
    if (this.petId && !!this.order?.id && !this.order.groups?.map(({ pet }) => pet?.id)?.includes(this.petId)) {
      this.$router.push({ name: 'TagStore' });
    }
  },
  destroyed() {
    window.removeEventListener('resize', this.windowResize);
  },
  methods: {
    onStyleChange() {
      if (this.productStyleCode !== this.style && this.styleId) {
        this.$store.dispatch('essentialProduct/setStyleByCode', { styleCode: this.style, groupId: this.groupId });
      }
    },
    validateBeforeAddItem() {
      this.$refs.essential_style_form
        ?.validate()
        .then(async (success) =>
          !success ? this.scrollToError(this.$refs.essential_style_form) : this.$nextTick(() => this.addToBasket())
        );
    },
    addToBasket() {
      this.addItem();
    },
    addItem() {
      if (!this.selectedProductId) return;
      this.analyticsTrackAddItem();
      if (this.user && this.user.id) {
        let details = {
          productId: this.selectedProductId,
          petId: this.pet?.id,
        };
        let items = Array.from({ length: this.quantity }, () => {
          return details;
        });
        let addItemRedirectTo = null;
        addItemRedirectTo = { name: 'EssentialsStore' };
        if (this.determineRedirectRoute) {
          addItemRedirectTo = { name: this.determineRedirectRoute };
        }
        this.$store
          .dispatch('order/addLocalItems', {
            vm: this,
            orderId: this.orderId,
            items: items,
            fetchOrderOnSuccess: true,
          })
          .then(() => {
            if (addItemRedirectTo) {
              this.$router.push(addItemRedirectTo);
              if (!this.determineRedirectRoute) {
                this.$emit('showSideBasket');
              }
            }
          });
      } else {
        const self = this;
        const data = {
          style: this.$route.params.id,
          productId: this.selectedProductId,
          color: this.selectedProduct.color || null,
          petId: this.$data.pet?.id,
          size: this.selectedProduct.size || null,
        };
        this.$store.dispatch('order/storeLocalItems', { data: [data], quantity: this.quantity }).then(() => {
          this.notifySuccess(
            `Item added. <br class="d-block d-md-none" /><a href="${this.generateFullUrl(
              '/basket'
            )}" class="text-primary"><u>${self.$t('basket.viewBasket')}</u></a> | <a href="${this.generateFullUrl(
            )}" class="text-primary"><u>${self.$t('basket.checkout')}</u></a>`,
            {
              family: 'AddItem',
              dismissSecs: 10,
              dismissible: true,
              isHtmlMessage: true,
              class: 'text-center',
              text: { class: 'text-secondary' },
              $callback: this,
              callback: function ({ localBasketCount, generateFullUrl, notifySuccess }) {
                notifySuccess(
                  `${localBasketCount} ${localBasketCount > 1 ? 'Items' : 'Item'}. <a href="${generateFullUrl(
                    '/basket'
                  )}" class="text-primary"><u>${self.$t('basket.viewBasket')}</u></a> | <a href="${generateFullUrl(
                    `/login/store?redirect_path=%2Fstore`
                  )}" class="text-primary"><u>${self.$t('basket.checkout')}</u></a>`,
                  {
                    family: 'AddItem',
                    icon: null,
                    dismissSecs: -1,
                    dismissible: true,
                    isHtmlMessage: true,
                    class: 'text-center',
                    text: { class: 'text-secondary' },
                  }
                );
              },
            }
          );
        });
        this.$router.push({ name: 'EssentialsStore' });
        this.$emit('showSideBasket');
      }
    },
    assignToPet(selectedPet) {
      if (!selectedPet?.id) return;
      this.pet = selectedPet;
    },
    analyticsTrackAddItem() {
      if (window?.dataLayer && this.selectedProduct) {
        window?.dataLayer?.push({
          ecommerce: null,
        });
        const items = [];
        const _itemData = {
          quantity: this.quantity,
          coupon: this.order?.discount?.code || '',
          currency: `${this.order?.city?.country || 'US'}D`,
          item_list_id: this.productStyle?.id || '',
          item_list_name: this.productStyle?.code?.toUpperCase() || '',
        };
        items.push({
          ..._itemData,
          item_id: this.selectedProduct?.id || '',
          item_name: this.selectedProduct?.name?.toUpperCase() || '',
          item_category: this.selectedProduct?.type?.toUpperCase() || '',
          price: Number(this.selectedProduct?.discountedPrice || this.selectedProduct?.price || 0).toFixed(2),
        });
        window.dataLayer.push({
          event: 'add_to_cart',
          ecommerce: {
            items: items,
            currency: `${this.order?.city?.country || 'US'}D`,
            value: Number(items?.reduce((p, c) => p + Number(c.price), 0)).toFixed(2),
          },
        });
      }
    },
    windowResize() {
      this.windowWidth = window.innerWidth;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@docupet/component-library/src/assets/styles/_variables';
.combo-img {
  border-radius: 10px;
  border: 2px solid $dp-gray-5;
  height: auto;
  width: 100%;
}
.collapsed > .when-open,
.not-collapsed > .when-closed {
  display: none;
}
.quantity-select {
  width: 6.5em;
}
</style>
